/**
 * B52(d) — the channel theme, painted.
 *
 * The mint response (session.guest) may carry the channel's brand theme:
 * color-role overrides plus a logo. Colors become the same --transact-*
 * custom properties @transact/ui's tokens.css already reads, so the brand
 * flows through every component without a single forked style.
 *
 * PRECEDENCE (pinned): the host page owns its embed. In embedded mode the
 * B31a bridge's init theme hints win over the channel theme for every var
 * the host sets — in BOTH arrival orders, because init and mint race. The
 * sink remembers which vars the host claimed; the channel fills the rest.
 *
 * THE SCHEME FOLLOWS THE SURFACE (B501, LEARNED #280): the twelve roles are
 * only the tokens a theme can NAME. tokens.css derives dozens more from the
 * scheme block — ink-soft (the outline button's hover), surface-2, text-soft,
 * the field and rail washes — and the storefront boots DARK whatever the OS
 * says (B403 #1). A brand that pins a light surface over the dark block got
 * a light card whose buttons hovered to the dark set's ink-soft under navy
 * text (Navy Pier, 2026-09-22). So a theme that names `surface` (or `bg`)
 * also picks the scheme block its luminance belongs to, and every derived
 * token follows the brand instead of the boot default.
 */
import type { VenueFacts } from "./footFacts.js";
import { CHANNEL_THEME_COLOR_ROLES } from "@transact/contracts";
import type { StorageLike } from "./session.js";

/** Structural mirror of the wire shape (contracts zod-validates it server-side). */
export interface ChannelThemeLike {
  readonly logoUrl?: string;
  /** B403 #26 (a) — the channel's hand-written storefront headline (75:2). */
  readonly headline?: string;
  readonly colors?: Partial<Record<(typeof CHANNEL_THEME_COLOR_ROLES)[number], string>>;
}

export interface StyleSetter {
  setProperty(name: string, value: string): void;
}

function kebab(role: string): string {
  return role.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}

/** Role overrides → --transact-* pairs, vocabulary order, colors only. */
export function channelThemeCssVars(theme: ChannelThemeLike | undefined): readonly (readonly [string, string])[] {
  const colors = theme?.colors;
  if (colors === undefined) return [];
  return CHANNEL_THEME_COLOR_ROLES.filter((role) => colors[role] !== undefined).map(
    (role) => [`--transact-${kebab(role)}`, colors[role] as string] as const,
  );
}

export interface ThemeSink {
  /** B31a host init hints: these vars are the host's for good. */
  applyHostVars(vars: readonly (readonly [string, string])[]): void;
  /** Channel vars: applied only where the host has not spoken. */
  applyChannelVars(vars: readonly (readonly [string, string])[]): void;
}

export type ThemeScheme = "light" | "dark";

/** Where tokens.css keys its scheme block (data-transact-theme). */
export interface SchemeRoot {
  setAttribute(name: string, value: string): void;
}

/** WCAG relative luminance of a #rgb/#rrggbb; undefined for anything else. */
function luminance(hex: string): number | undefined {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (m === null) return undefined;
  const h = m[1]!.length === 3 ? m[1]!.split("").map((c) => c + c).join("") : m[1]!;
  const channel = (i: number): number => {
    const c = Number.parseInt(h.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

/**
 * The scheme block a set of vars belongs to, decided by the SURFACE it
 * names (the card's own fill; `bg` when only the canvas is named): a light
 * surface reads as the light sheet, a dark one as the dark. Vars that name
 * neither leave the boot scheme alone.
 */
export function schemeForVars(vars: readonly (readonly [string, string])[]): ThemeScheme | undefined {
  const named = new Map(vars);
  const surface = named.get("--transact-surface") ?? named.get("--transact-bg");
  if (surface === undefined) return undefined;
  const l = luminance(surface);
  if (l === undefined) return undefined;
  return l > 0.5 ? "light" : "dark";
}

/* ------------------------------------------------------------------ B548 */

/** mix a toward b by t (0..1), #rrggbb only. */
function mixHex(a: string, b: string, t: number): string {
  const ch = (h: string, i: number): number => Number.parseInt(h.slice(i, i + 2), 16);
  const ha = a.slice(1).length === 3 ? a.slice(1).split("").map((c) => c + c).join("") : a.slice(1);
  const hb = b.slice(1).length === 3 ? b.slice(1).split("").map((c) => c + c).join("") : b.slice(1);
  const mixed = [0, 2, 4].map((i) => Math.round(ch(ha, i) + (ch(hb, i) - ch(ha, i)) * t));
  return `#${mixed.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  if (la === undefined || lb === undefined) return 1;
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

const DERIVED_TEXT_ROLES: readonly (readonly [string, number])[] = [
  ["--transact-text-soft", 0.2],
  ["--transact-label-ink", 0.28],
  ["--transact-muted", 0.32],
  ["--transact-label-dim", 0.45],
  ["--transact-text-faint", 0.5],
];

/**
 * B548 — a tenant theme's extended text roles, derived from ITS OWN text
 * and surface rather than the package block's fixed tints: each role is
 * the text mixed toward the surface by its register's amount, pulled back
 * toward the text until it clears 4.5:1 (WCAG AA). Pathik's Navy Pier
 * verdict ("hard to read with light text") was exactly a fixed pale tint
 * under a brand scheme; deriving from the brand's ink keeps the hierarchy
 * AND the floor whatever the palette. A text that cannot read on its own
 * surface falls back to near-black/near-white by the surface's luminance.
 */
export function deriveTextRoles(text: string, surface: string): Record<string, string> {
  const l = luminance(surface);
  if (l === undefined || luminance(text) === undefined) return {}; // non-hex — derive nothing, fail closed
  const base = contrastRatio(text, surface) >= 4.5 ? text : l !== undefined && l > 0.5 ? "#111827" : "#f4f6fb";
  const out: Record<string, string> = {};
  for (const [role, tint] of DERIVED_TEXT_ROLES) {
    let t = tint;
    let v = mixHex(base, surface, t);
    while (t > 0 && contrastRatio(v, surface) < 4.5) {
      t = Math.max(0, t - 0.04);
      v = mixHex(base, surface, t);
    }
    out[role] = v;
  }
  return out;
}

/* ------------------------------------------------------------------ B576 (b) */

/**
 * B576 (b) — the roles a brand names FOR ITS OWN SURFACE. Navy Pier's
 * `text #263c79` is right on its white card and wrong on the dark block;
 * written inline it outranked both scheme blocks, so a ☾ press painted navy
 * on the dark ground. These roles (and B548's text roles, derived from the
 * same text/surface pair) belong to the scheme their surface picked. The
 * scheme-NEUTRAL roles — accent, ink/on-ink — read on either block and stay
 * inline.
 */
export const SCHEME_BOUND_ROLES: readonly string[] = [
  "--transact-bg",
  "--transact-surface",
  "--transact-text",
  "--transact-muted",
  "--transact-border",
  ...DERIVED_TEXT_ROLES.map(([role]) => role),
];
const BOUND = new Set(SCHEME_BOUND_ROLES);

/** The one stylesheet rule the sink owns (rewritten whole on every apply). */
export interface BrandSheet {
  replace(cssText: string): void;
}

/**
 * The document's brand sheet: ONE `<style data-transact-brand>` appended to
 * head (found again, never duplicated). The rule doubles `:root` so it beats
 * tokens.css's scheme blocks whatever order the stylesheets land in; host
 * vars stay inline and so still win (§9.2).
 */
export function brandSheetFor(doc: Document): BrandSheet {
  let el = doc.head.querySelector<HTMLStyleElement>("style[data-transact-brand]");
  if (el === null) {
    el = doc.createElement("style");
    el.setAttribute("data-transact-brand", "");
    doc.head.appendChild(el);
  }
  const style = el;
  return {
    replace(cssText) {
      style.textContent = cssText;
    },
  };
}

function scopedRule(scheme: ThemeScheme, vars: ReadonlyMap<string, string>): string {
  if (vars.size === 0) return "";
  const body = [...vars].map(([name, value]) => `  ${name}: ${value};`).join("\n");
  return `:root:root[data-transact-theme="${scheme}"] {\n${body}\n}\n`;
}

export function createThemeSink(style: StyleSetter, root?: SchemeRoot, sheet?: BrandSheet): ThemeSink {
  const hostOwned = new Set<string>();
  /* B576 (b): the scheme-bound channel roles, held for the sheet's one rule,
     and the scheme they are bound to (the last surface a theme named). */
  const bound = new Map<string, string>();
  let boundScheme: ThemeScheme | undefined;
  const follow = (vars: readonly (readonly [string, string])[]): void => {
    const scheme = schemeForVars(vars);
    if (scheme !== undefined) root?.setAttribute("data-transact-theme", scheme);
  };
  return {
    applyHostVars(vars) {
      for (const [name, value] of vars) {
        hostOwned.add(name);
        style.setProperty(name, value);
      }
      follow(vars);
    },
    applyChannelVars(vars) {
      const applied: (readonly [string, string])[] = [];
      for (const [name, value] of vars) {
        if (hostOwned.has(name)) continue;
        applied.push([name, value]);
      }
      // B548: a theme that names both its text and its surface also gets
      // its EXTENDED text roles (text-soft, label-ink, label-dim, …)
      // derived from that pair to the AA floor — the package block's fixed
      // tints are tuned to the package palette, not the brand's. Roles the
      // theme named itself, and roles the host owns, stand.
      const namedNow = new Map(applied);
      const derived: (readonly [string, string])[] = [];
      const dText = namedNow.get("--transact-text");
      const dSurface = namedNow.get("--transact-surface");
      if (dText !== undefined && dSurface !== undefined) {
        for (const [role, value] of Object.entries(deriveTextRoles(dText, dSurface))) {
          if (hostOwned.has(role) || namedNow.has(role)) continue;
          derived.push([role, value]);
        }
      }
      // B576 (b): the surface this theme names decides the scheme its bound
      // roles belong to; with no sheet, or no scheme to bind to, they paint
      // inline as before.
      const scheme = schemeForVars(applied) ?? boundScheme;
      const toSheet = sheet !== undefined && scheme !== undefined;
      for (const [name, value] of applied.concat(derived)) {
        if (toSheet && BOUND.has(name)) bound.set(name, value);
        else style.setProperty(name, value);
      }
      if (toSheet) {
        boundScheme = scheme;
        sheet.replace(scopedRule(scheme, bound));
      }
      // The host's surface owns the scheme too: a channel that could not paint
      // the surface must not flip the block the host's surface sits in.
      if (hostOwned.has("--transact-surface") || hostOwned.has("--transact-bg")) return;
      follow(applied);
    },
  };
}

export function applyChannelTheme(sink: ThemeSink, theme: ChannelThemeLike | undefined): void {
  sink.applyChannelVars(channelThemeCssVars(theme));
}

/* ------------------------------------------------------------------ B144 */

const THEME_KEY = "transact.storefront.channelTheme";
const HEX_SHAPE = /^#[0-9a-fA-F]{3,8}$/;

/**
 * B144 — the theme persists NEXT TO the token it arrived with. A reload
 * with a live session never mints, so the brand must not depend on the
 * mint firing: the last mint's theme is remembered here and recalled at
 * boot. Persisted storage is garble-prone, so recall sanitizes strictly
 * (known roles, hex values only, refused whole when nothing survives) —
 * fail closed to the default brand, never a half-paint or a CSS smuggle.
 */
export function rememberChannelTheme(storage: StorageLike | null, theme: ChannelThemeLike): void {
  try {
    storage?.setItem(THEME_KEY, JSON.stringify({ logoUrl: theme.logoUrl, headline: theme.headline, colors: theme.colors }));
  } catch {
    /* memory-only sessions just re-mint */
  }
}

export function recallChannelTheme(storage: StorageLike | null): ChannelThemeLike | undefined {
  let raw: string | null | undefined;
  try {
    raw = storage?.getItem(THEME_KEY);
  } catch {
    return undefined;
  }
  if (raw === null || raw === undefined) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (parsed === null || typeof parsed !== "object") return undefined;
  const record = parsed as { logoUrl?: unknown; headline?: unknown; colors?: unknown };
  const logoUrl = typeof record.logoUrl === "string" ? record.logoUrl : undefined;
  /* B403 #26 (a) — same strict recall: a plain short line or nothing. */
  const headline =
    typeof record.headline === "string" && record.headline.trim().length > 0 && record.headline.length <= 120
      ? record.headline
      : undefined;
  let colors: Record<string, string> | undefined;
  if (record.colors !== null && typeof record.colors === "object") {
    const entries = CHANNEL_THEME_COLOR_ROLES.flatMap((role) => {
      const value = (record.colors as Record<string, unknown>)[role];
      return typeof value === "string" && HEX_SHAPE.test(value) ? [[role, value] as const] : [];
    });
    if (entries.length > 0) colors = Object.fromEntries(entries);
  }
  if (logoUrl === undefined && colors === undefined && headline === undefined) return undefined;
  return {
    ...(logoUrl !== undefined ? { logoUrl } : {}),
    ...(headline !== undefined ? { headline } : {}),
    ...(colors !== undefined ? { colors: colors as NonNullable<ChannelThemeLike["colors"]> } : {}),
  };
}

/* ------------------------------------------------------------------ B397 leg (3) */

const PUBLISHABLE_KEY_KEY = "transact.storefront.publishableKey";
/* Stripe's publishable-key shape and ONLY it: `pk_` prefixed. A secret key
   (`sk_`) is refused even if one somehow landed in storage — fail closed,
   and never let a paste-mistake become a page fact. */
const PUBLISHABLE_SHAPE = /^pk_[A-Za-z0-9_]+$/;

/**
 * B144's argument, worn by the payment seam: the deferred cart & pay page
 * needs Stripe(pk) at PAGE LOAD, the key rides ONLY the session.guest mint,
 * and a reload with a live token never mints. Persist beside the token,
 * recall at boot, sanitize strictly on the way out.
 */
export function rememberPublishableKey(storage: StorageLike | null, publishableKey: string): void {
  try {
    storage?.setItem(PUBLISHABLE_KEY_KEY, publishableKey);
  } catch {
    /* memory-only sessions just re-mint */
  }
}

export function recallPublishableKey(storage: StorageLike | null): string | undefined {
  let raw: string | null | undefined;
  try {
    raw = storage?.getItem(PUBLISHABLE_KEY_KEY);
  } catch {
    return undefined;
  }
  if (raw === null || raw === undefined) return undefined;
  return PUBLISHABLE_SHAPE.test(raw) ? raw : undefined;
}

/* ------------------------------------------------------------------ B91 GAP-6 */

const STRIPE_ACCOUNT_KEY = "transact.storefront.stripeAccount";
/* Connected-account shape and ONLY it — anything else in storage is refused. */
const STRIPE_ACCOUNT_SHAPE = /^acct_[A-Za-z0-9]+$/;

/** B91 GAP-6 — the mint-delivered connected account, persisted beside the publishable key (same B144 argument). */
export function rememberStripeAccount(storage: StorageLike | null, stripeAccount: string): void {
  try {
    storage?.setItem(STRIPE_ACCOUNT_KEY, stripeAccount);
  } catch {
    /* memory-only sessions just re-mint */
  }
}

export function recallStripeAccount(storage: StorageLike | null): string | undefined {
  let raw: string | null | undefined;
  try {
    raw = storage?.getItem(STRIPE_ACCOUNT_KEY);
  } catch {
    return undefined;
  }
  if (raw === null || raw === undefined) return undefined;
  return STRIPE_ACCOUNT_SHAPE.test(raw) ? raw : undefined;
}

/* ------------------------------------------------------------------ B403 #26 */

const BUSINESS_NAME_KEY = "transact.storefront.businessName";
const VENUE_KEY = "transact.storefront.venue";
const VENUE_FACT_KEYS = ["venueAddress", "venuePhone", "venueNote"] as const;

/**
 * B653 (c) — where guests find the venue rides ONLY the session.guest mint
 * (the business name's delivery): persist beside the token, recall at boot,
 * and keep only plain short lines (a stored stranger is dropped fact by fact).
 */
export function rememberVenue(storage: StorageLike | null, venue: VenueFacts): void {
  try {
    storage?.setItem(VENUE_KEY, JSON.stringify(venue));
  } catch {
    /* memory-only sessions just re-mint */
  }
}

export function recallVenue(storage: StorageLike | null): VenueFacts {
  let raw: string | null | undefined;
  try {
    raw = storage?.getItem(VENUE_KEY);
  } catch {
    return {};
  }
  if (raw === null || raw === undefined) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (parsed === null || typeof parsed !== "object") return {};
  const out: Record<string, string> = {};
  for (const key of VENUE_FACT_KEYS) {
    const v = (parsed as Record<string, unknown>)[key];
    if (typeof v === "string" && v.trim().length > 0 && v.length <= 120) out[key] = v;
  }
  return out;
}

/**
 * B403 #26 (c)/(g) — the tenant's name rides ONLY the session.guest mint,
 * and a reload with a live token never mints: persist beside the token,
 * recall at boot, sanitize strictly (a plain short line or nothing).
 */
export function rememberBusinessName(storage: StorageLike | null, businessName: string): void {
  try {
    storage?.setItem(BUSINESS_NAME_KEY, businessName);
  } catch {
    /* memory-only sessions just re-mint */
  }
}

const TENANT_TYPE_KEY = "transact.storefront.tenantType";
const TENANT_TYPE_SHAPE = /^(?:FERRY|SHOW|PARK|ATTRACTION|RETAIL)$/;

/**
 * B403 #22-extension — the tenant's type rides ONLY the session.guest
 * mint (same delivery as the name above): persist beside the token,
 * recall at boot, and refuse any stored stranger (the closed B126 set).
 */
export function rememberTenantType(storage: StorageLike | null, tenantType: string): void {
  try {
    storage?.setItem(TENANT_TYPE_KEY, tenantType);
  } catch {
    /* memory-only sessions just re-mint */
  }
}

/** B614 (a) — a type-less mint forgets the last tenant's type (one tab, two tenants). */
export function forgetTenantType(storage: StorageLike | null): void {
  try {
    storage?.removeItem(TENANT_TYPE_KEY);
  } catch {
    /* memory-only sessions just re-mint */
  }
}

export function recallTenantType(storage: StorageLike | null): string | undefined {
  let raw: string | null | undefined;
  try {
    raw = storage?.getItem(TENANT_TYPE_KEY);
  } catch {
    return undefined;
  }
  if (raw === null || raw === undefined) return undefined;
  return TENANT_TYPE_SHAPE.test(raw) ? raw : undefined;
}

const CLOCK_KEY = "transact.storefront.clock";
const TIME_FORMAT_SHAPE = /^(?:12h|24h)$/;
const TIMEZONE_SHAPE = /^[A-Za-z0-9_+\-/]{1,64}$/;

/** B621 — the tenant's clock facts (timezone + timeFormat), as the mint delivers them. */
export interface ClockFacts {
  readonly timezone?: string;
  readonly timeFormat?: "12h" | "24h";
}

/**
 * B621 — the clock facts ride ONLY the session.guest mint (the tenantType
 * seam): persist beside the token, recall at boot, refuse any stored
 * stranger. An empty pair stores nothing and recalls as nothing.
 */
export function rememberClock(storage: StorageLike | null, facts: ClockFacts): void {
  try {
    if (facts.timezone === undefined && facts.timeFormat === undefined) {
      storage?.removeItem(CLOCK_KEY);
      return;
    }
    storage?.setItem(CLOCK_KEY, JSON.stringify(facts));
  } catch {
    /* memory-only sessions just re-mint */
  }
}

/** B614 (a)'s shape — a clock-less mint forgets the last tenant's clock (one tab, two tenants). */
export function forgetClock(storage: StorageLike | null): void {
  try {
    storage?.removeItem(CLOCK_KEY);
  } catch {
    /* memory-only sessions just re-mint */
  }
}

export function recallClock(storage: StorageLike | null): ClockFacts | undefined {
  let raw: string | null | undefined;
  try {
    raw = storage?.getItem(CLOCK_KEY);
  } catch {
    return undefined;
  }
  if (raw === null || raw === undefined) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (typeof parsed !== "object" || parsed === null) return undefined;
  const rec = parsed as Record<string, unknown>;
  const timezone = typeof rec["timezone"] === "string" && TIMEZONE_SHAPE.test(rec["timezone"]) ? rec["timezone"] : undefined;
  const timeFormat =
    typeof rec["timeFormat"] === "string" && TIME_FORMAT_SHAPE.test(rec["timeFormat"])
      ? (rec["timeFormat"] as "12h" | "24h")
      : undefined;
  // a stored stranger (a shape neither field survives) recalls as nothing
  if (timezone === undefined && timeFormat === undefined) return undefined;
  if (typeof rec["timeFormat"] === "string" && timeFormat === undefined) return undefined;
  return { ...(timezone === undefined ? {} : { timezone }), ...(timeFormat === undefined ? {} : { timeFormat }) };
}

export function recallBusinessName(storage: StorageLike | null): string | undefined {
  let raw: string | null | undefined;
  try {
    raw = storage?.getItem(BUSINESS_NAME_KEY);
  } catch {
    return undefined;
  }
  if (raw === null || raw === undefined) return undefined;
  return raw.trim().length > 0 && raw.length <= 120 ? raw : undefined;
}

/* B524 leg 2 — the tenant's GTM container, remembered like the business
   name: a reload with a live token does not re-mint, and the hosted page
   must still load the container. Recall re-checks GTM's shape — the value
   becomes a script URL, so a tampered storage value loads nothing. */
const GTM_CONTAINER_KEY = "transact.storefront.gtmContainerId";
const GTM_SHAPE = /^GTM-[A-Z0-9]{4,12}$/;

export function rememberGtmContainerId(storage: StorageLike | null, containerId: string): void {
  try {
    storage?.setItem(GTM_CONTAINER_KEY, containerId);
  } catch {
    /* memory-only sessions just re-mint */
  }
}

export function recallGtmContainerId(storage: StorageLike | null): string | undefined {
  let raw: string | null | undefined;
  try {
    raw = storage?.getItem(GTM_CONTAINER_KEY);
  } catch {
    return undefined;
  }
  return typeof raw === "string" && GTM_SHAPE.test(raw) ? raw : undefined;
}

/* ------------------------------------------------------------------ B549 */

const GROUP_THRESHOLD_KEY = "transact.storefront.groupThreshold";

/** B549 — the group threshold persists beside the token it arrived with (B144's argument). */
export function rememberGroupThreshold(storage: StorageLike | null, threshold: number): void {
  try {
    storage?.setItem(GROUP_THRESHOLD_KEY, String(threshold));
  } catch {
    /* memory-only sessions just re-mint */
  }
}

const GROUPS_CONTACT_KEY = "transact.storefront.groupsContactName";

/** B549 (9) — the desk's first name persists beside the token it arrived with (B144). */
export function rememberGroupsContactName(storage: StorageLike | null, name: string): void {
  try {
    storage?.setItem(GROUPS_CONTACT_KEY, name);
  } catch {
    /* memory-only sessions just re-mint */
  }
}

/** Strict recall: a 1–40 character name after trimming, or nothing — the desk then speaks as the desk. */
export function recallGroupsContactName(storage: StorageLike | null): string | undefined {
  let raw: string | null | undefined;
  try {
    raw = storage?.getItem(GROUPS_CONTACT_KEY);
  } catch {
    return undefined;
  }
  if (typeof raw !== "string") return undefined;
  const name = raw.trim();
  return name.length > 0 && name.length <= 40 ? name : undefined;
}

export function recallGroupThreshold(storage: StorageLike | null): number | undefined {
  let raw: string | null | undefined;
  try {
    raw = storage?.getItem(GROUP_THRESHOLD_KEY);
  } catch {
    return undefined;
  }
  if (raw === null || raw === undefined) return undefined;
  const parsed = Number(raw);
  /* strict recall: a positive safe integer or nothing — fail closed to no interrupt. */
  return Number.isSafeInteger(parsed) && parsed > 0 && parsed <= 100_000 ? parsed : undefined;
}

/* ------------------------------------------------------------------ B818 */
const MEMBERSHIPS_KEY = "transact.storefront.memberships";
/** B818 — a plan is on sale here: the MEMBERSHIPS door is drawn from boot on the next load (the mint + plan read decide again). */
export function rememberMembershipsOnSale(storage: StorageLike | null, on: boolean): void {
  try {
    if (on) storage?.setItem(MEMBERSHIPS_KEY, "1");
    else storage?.removeItem(MEMBERSHIPS_KEY);
  } catch {
    /* memory-only */
  }
}
export function recallMembershipsOnSale(storage: StorageLike | null): boolean {
  try {
    return storage?.getItem(MEMBERSHIPS_KEY) === "1";
  } catch {
    return false;
  }
}
